import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button } from '@workspace/ui/components/Button'
import { ChevronLeft } from 'lucide-react'
import { RoleForm, RoleFormData } from '@/shared/components/form/RoleForm'
import { api, type RoleCreate } from '@/shared/lib/api'
import { z } from 'zod'
import { toast } from '@workspace/ui/components/Sonner'
import { generateRoleName } from '@/shared/lib/utils'
import {
    buildPermissionStringsFromForm,
    parsePermissionStringsToForm,
    getDefaultPermissionRows,
} from '@/shared/lib/permissions'
import { requirePermissionBeforeLoad } from '@/shared/lib/authorization/routeAuth'

const roleSearchSchema = z.object({
    id: z.string().optional(),
})

export const Route = createFileRoute('/users/roles/new')({
    component: AddRolePage,
    validateSearch: roleSearchSchema,
    beforeLoad: requirePermissionBeforeLoad('operate', 'system'),
})

function AddRolePage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { id: duplicateId } = Route.useSearch()

    // Fetch role data if duplicating
    const { data: duplicateRole, isLoading: isDuplicateLoading } = useQuery({
        queryKey: ['role', duplicateId],
        queryFn: () => api.role.findOne(duplicateId!),
        enabled: !!duplicateId,
        staleTime: 0,
    })

    // Create role mutation
    const createRoleMutation = useMutation({
        mutationFn: (payload: RoleCreate) => api.role.create(payload),
        onSuccess: () => {
            toast.success({
                title: t('common.status.success'),
                description: t('roles.create.success'),
            })
            navigate({ to: '/users/roles' })
        },
        onError: (error: any) => {
            if (error?.status === 403) {
                return toast.error({
                    title: 'Bạn không có quyền thực hiện hành động này.',
                })
            }
            if (error?.response?.data?.message?.includes('Name already exists')) {
                return toast.error({
                    title: 'Tên vai trò đã tồn tại. Vui lòng kiểm tra lại.',
                })
            }

            toast.error({
                title: t('common.status.error'),
                description: t('roles.create.error'),
            })
            console.error('Create role error:', error)
        },
        meta: {
            skipGlobalErrorHandler: true,
        },
    })

    const onSubmit = async (data: RoleFormData) => {
        const permissionKeys = buildPermissionStringsFromForm(data.permissions ?? [])

        const payload: RoleCreate = {
            name: generateRoleName(data.roleName),
            displayName: data.roleName.trim(),
            description: (data.description || '').trim(),
            level: data.level ? parseInt(data.level) : null,
            isSystemRole: false,
            isActive: true,
            permissionKeys,
        }

        await createRoleMutation.mutateAsync(payload)
    }

    const handleSaveClick = () => {
        // Trigger form submission via a ref or external trigger
        const form = document.querySelector('form')
        if (form) {
            form.requestSubmit()
        }
    }

    // Prepare default values for the form
    const defaultValues = duplicateRole
        ? {
              roleName: duplicateRole.displayName,
              description: duplicateRole.description || '',
              level: duplicateRole.level && duplicateRole.level > 0 ? duplicateRole.level.toString() : '',
              permissions: parsePermissionStringsToForm(duplicateRole.permissions || []),
          }
        : {
              roleName: '',
              description: '',
              permissions: getDefaultPermissionRows(),
          }

    return (
        // Cho phép cuộn khi form dài trên màn hình nhỏ
        <div className="flex h-full flex-col">
            {/* Header */}
            <div className="flex gap-3 items-center mb-4 shrink-0">
                <Link className="p-2 border rounded-[8px]" to="/users/roles">
                    <ChevronLeft size={20} />
                </Link>
                <h1 className="text-2xl font-bold">{t('roles.create.title')}</h1>
                <Button
                    variant="default"
                    onPress={handleSaveClick}
                    isDisabled={createRoleMutation.isPending || isDuplicateLoading}
                    className="min-w-[100px] ml-auto"
                >
                    {createRoleMutation.isPending ? t('common.actions.saving') : t('common.actions.save')}
                </Button>
            </div>

            <RoleForm
                defaultValues={defaultValues}
                onSubmit={onSubmit}
                isSubmitting={createRoleMutation.isPending || isDuplicateLoading}
            />
        </div>
    )
}
